import { useState, useRef, useEffect } from 'react';
import { showToast } from './Toasts';

interface EmailCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
}

export function EmailCaptureModal({ isOpen, onClose, onSubmit }: EmailCaptureModalProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setIsSubmitting(false);
      // Focus input after animation
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      showToast('Please enter a valid email address', 'warning');
      return;
    }
    setIsSubmitting(true);
    try {
      onSubmit(email);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm mx-4 rounded-3xl glass-modal shadow-3d-card animate-modal-enter"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="mb-5 flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Where should we send your preview?</h3>
              <p className="mt-1 text-sm text-slate-500">
                Enter your email to generate and save your cabinet preview.
              </p>
            </div>
            <button
              onClick={onClose}
              className="ml-2 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="email-capture" className="sr-only">Email address</label>
              <input
                ref={inputRef}
                id="email-capture"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-inner-glow focus:border-bss-blue focus:ring-2 focus:ring-bss-blue/20 focus:outline-none transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="w-full rounded-xl bg-bss-blue py-3 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-bss-blue-dark hover:shadow-blue-600/35 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                'Generate My Preview'
              )}
            </button>

            <p className="mt-3 text-center text-[11px] text-slate-400">
              We won't spam you. Your email is only used to save your preview and send estimate requests.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
